import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchDashboardOrderSummary,
  fetchDashboardWalletBalance,
  fetchGatewaysData,
  fetchTradersData
} from '../../services/dataApi';
import { formatDateInput, getBrowserTimezone, toRangeBoundary } from '../../components/OrderTools';
import './LiveDashboard.css';

const REFRESH_INTERVAL_MS = 30 * 1000;
const MIN_VISIBLE_WALLET_BALANCE = 10;

const emptyOrderSummary = {
  payin: { all: 0, completed: 0, allCount: 0, completedCount: 0 },
  payout: { all: 0, completed: 0, allCount: 0, completedCount: 0 }
};

const extractList = (payload) => {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  const firstOperation = payload?.ops?.[0];
  if (Array.isArray(firstOperation?.list)) return firstOperation.list;
  if (Array.isArray(firstOperation?.data?.list)) return firstOperation.data.list;
  return [];
};

const toNumber = (value) => {
  const number = Number.parseFloat(value);
  return Number.isFinite(number) ? number : 0;
};

const readBalance = (entity) => {
  const balance = entity?.balance || entity?.Balance || entity?.BALANCE || {};
  if (typeof balance === 'number' || typeof balance === 'string') {
    const total = toNumber(balance);
    return { current: total, pending: 0, total };
  }

  const current = toNumber(balance.current || balance.Current || balance.CURRENT);
  const pending = toNumber(balance.pending || balance.Pending || balance.PENDING);
  const totalValue = balance.total || balance.Total || balance.TOTAL;
  return {
    current,
    pending,
    total: totalValue === undefined || totalValue === null || totalValue === ''
      ? current + pending
      : toNumber(totalValue)
  };
};

const sumBalances = (items) => items.reduce((summary, item) => {
  const balance = readBalance(item);
  return {
    current: summary.current + balance.current,
    pending: summary.pending + balance.pending,
    total: summary.total + balance.total
  };
}, { current: 0, pending: 0, total: 0 });

const groupGatewayBalances = (items) => {
  const grouped = items.reduce((result, item) => {
    const currency = String(item.currency || item.Currency || item.CURRENCY || 'Unknown').trim();
    result[currency] = (result[currency] || 0) + readBalance(item).total;
    return result;
  }, {});

  return Object.entries(grouped)
    .map(([currency, total]) => ({ currency, total }))
    .filter(({ total }) => total !== 0)
    .sort((a, b) => b.total - a.total);
};

const isTrader = (item) => String(item.role || item.Role || item.ROLE || '').toLowerCase() === 'trader';

const formatAmount = (value) => Number(value || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const formatPercent = (part, total) => {
  const denominator = Number(total || 0);
  if (denominator <= 0) return '0%';
  return `${(Number(part || 0) / denominator * 100).toLocaleString('en-US', {
    maximumFractionDigits: 1
  })}%`;
};

const getPercentageValue = (part, total) => {
  const denominator = Number(total || 0);
  if (denominator <= 0) return 0;
  return Math.min(100, Math.max(0, Number(part || 0) / denominator * 100));
};

const getDateRange = (preset) => {
  const end = new Date();
  const start = new Date(end);

  if (preset === 'yesterday') {
    start.setDate(start.getDate() - 1);
    end.setDate(end.getDate() - 1);
  }
  if (preset === 'month') start.setDate(1);

  return { from: formatDateInput(start), to: formatDateInput(end) };
};

const MetricCard = ({ direction, summary }) => (
  <article className={`live-order-card live-order-card--${direction.toLowerCase()}`}>
    <div className="live-order-heading">
      <span className="live-index">{direction === 'PayIN' ? '01' : '02'}</span>
      <h2>{direction}</h2>
      <span className="live-currency">USDT</span>
    </div>
    <div className="live-order-main">
      <span>Completed volume</span>
      <strong>{formatAmount(summary.completed)} <small>USDT</small></strong>
    </div>
    <div className="live-order-footer">
      <div><span>Completed orders</span><strong>{Number(summary.completedCount || 0).toLocaleString('en-US')}</strong></div>
      <div><span>Total orders</span><strong>{Number(summary.allCount || 0).toLocaleString('en-US')}</strong></div>
      <div><span>Count rate</span><strong>{formatPercent(summary.completedCount, summary.allCount)}</strong></div>
      <div><span>Volume rate</span><strong>{formatPercent(summary.completed, summary.all)}</strong></div>
      <div><span>Total volume</span><strong>{formatAmount(summary.all)} <small>USDT</small></strong></div>
    </div>
  </article>
);

const LiveDashboard = () => {
  const [preset, setPreset] = useState('today');
  const [data, setData] = useState({
    orders: emptyOrderSummary,
    traders: { current: 0, pending: 0, total: 0 },
    traderCount: 0,
    gateways: [],
    gatewayCount: 0,
    wallet: null
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [now, setNow] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(Boolean(document.fullscreenElement));
  const [clockMode, setClockMode] = useState(false);
  const timezone = useMemo(() => getBrowserTimezone(), []);
  const range = useMemo(() => getDateRange(preset), [preset]);

  const loadData = useCallback(async ({ initial = false } = {}) => {
    if (initial) setLoading(true);
    else setRefreshing(true);
    setError('');

    const dateFrom = toRangeBoundary(range.from, false, timezone, '00:00:00');
    const dateTo = toRangeBoundary(range.to, true, timezone, '23:59:59');
    const results = await Promise.allSettled([
      fetchDashboardOrderSummary(dateFrom, dateTo),
      fetchTradersData(1, 1000),
      fetchGatewaysData(1, 1000),
      fetchDashboardWalletBalance()
    ]);

    const [ordersResult, tradersResult, gatewaysResult, walletResult] = results;
    setData((current) => {
      const traders = tradersResult.status === 'fulfilled'
        ? extractList(tradersResult.value).filter(isTrader)
        : null;
      const gateways = gatewaysResult.status === 'fulfilled'
        ? extractList(gatewaysResult.value)
        : null;

      return {
        orders: ordersResult.status === 'fulfilled'
          ? ordersResult.value?.data || emptyOrderSummary
          : current.orders,
        traders: traders ? sumBalances(traders) : current.traders,
        traderCount: traders ? traders.length : current.traderCount,
        gateways: gateways ? groupGatewayBalances(gateways) : current.gateways,
        gatewayCount: gateways ? gateways.length : current.gatewayCount,
        wallet: walletResult.status === 'fulfilled'
          ? walletResult.value?.data || null
          : current.wallet
      };
    });

    const failures = results.filter((result) => result.status === 'rejected');
    if (failures.length === results.length) {
      setError('Data is temporarily unavailable');
    } else if (failures.length > 0) {
      setError(`${failures.length} data source${failures.length === 1 ? '' : 's'} did not update`);
    }
    setUpdatedAt(new Date());
    setLoading(false);
    setRefreshing(false);
  }, [range.from, range.to, timezone]);

  useEffect(() => {
    loadData({ initial: true });
    const refreshTimer = window.setInterval(() => loadData(), REFRESH_INTERVAL_MS);
    return () => window.clearInterval(refreshTimer);
  }, [loadData]);

  useEffect(() => {
    const clockTimer = window.setInterval(() => setNow(new Date()), 1000);
    const handleFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      window.clearInterval(clockTimer);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const getControls = () => Array.from(document.querySelectorAll(
      clockMode
        ? '.live-clock-overlay [data-tv-control]'
        : '.live-dashboard [data-tv-control]:not([data-tv-clock-overlay])'
    )).sort((a, b) => Number(a.dataset.tvOrder || 0) - Number(b.dataset.tvOrder || 0));
    const focusClock = () => {
      document.querySelector(
        clockMode ? '[data-tv-clock-overlay]' : '[data-tv-clock]'
      )?.focus();
    };
    const initialFocus = window.requestAnimationFrame(focusClock);

    const handleRemoteNavigation = (event) => {
      const fallbackKeys = {
        37: 'ArrowLeft',
        38: 'ArrowUp',
        39: 'ArrowRight',
        40: 'ArrowDown'
      };
      const key = event.key || fallbackKeys[event.keyCode];

      if (clockMode) {
        if (key === 'Escape') {
          event.preventDefault();
          setClockMode(false);
        } else if (key?.startsWith('Arrow')) {
          event.preventDefault();
          focusClock();
        }
        return;
      }

      if (key === 'ArrowUp' || key === 'ArrowDown') {
        event.preventDefault();
        window.scrollBy({
          top: (key === 'ArrowDown' ? 1 : -1) * Math.max(260, window.innerHeight * 0.7),
          behavior: 'smooth'
        });
        return;
      }

      if (key !== 'ArrowLeft' && key !== 'ArrowRight') return;

      event.preventDefault();
      const controls = getControls();
      if (controls.length === 0) return;
      const currentIndex = controls.indexOf(document.activeElement);
      const step = key === 'ArrowRight' ? 1 : -1;
      const nextIndex = currentIndex < 0
        ? (step > 0 ? 0 : controls.length - 1)
        : (currentIndex + step + controls.length) % controls.length;
      controls[nextIndex].focus();
    };

    document.addEventListener('keydown', handleRemoteNavigation);
    return () => {
      window.cancelAnimationFrame(initialFocus);
      document.removeEventListener('keydown', handleRemoteNavigation);
    };
  }, [clockMode]);

  const toggleFullscreen = async () => {
    if (document.fullscreenElement) await document.exitFullscreen();
    else await document.documentElement.requestFullscreen();
  };

  const visibleWallets = Array.isArray(data.wallet?.wallets)
    ? data.wallet.wallets.filter((wallet) => Number(wallet.balance || 0) >= MIN_VISIBLE_WALLET_BALANCE)
    : [];

  return (
    <main className="live-dashboard">
      {clockMode && (
        <div className="live-clock-overlay" role="dialog" aria-modal="true" aria-label="Privacy clock">
          <button
            type="button"
            data-tv-control
            data-tv-clock-overlay
            data-tv-order="0"
            onClick={() => setClockMode(false)}
            aria-label="Return to dashboard"
          >
            <span className="live-privacy-label"><i aria-hidden="true" /> Privacy mode</span>
            <strong>{now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</strong>
            <span className="live-privacy-date">
              {now.toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
            </span>
            <small>Press OK to return</small>
          </button>
        </div>
      )}
      <header className="live-header">
        <div className="live-brand" aria-label="NadalPay operations">
          <span className="live-brand-mark" aria-hidden="true" />
          <div><strong>NADALPAY</strong><span>OPERATIONS</span></div>
        </div>

        <nav className="live-periods" aria-label="Dashboard period">
          {[
            ['today', 'Today'],
            ['yesterday', 'Yesterday'],
            ['month', 'This month']
          ].map(([value, label], index) => (
            <button
              type="button"
              key={value}
              data-tv-control
              data-tv-order={index + 1}
              className={preset === value ? 'is-active' : ''}
              aria-pressed={preset === value}
              onClick={() => setPreset(value)}
            >
              {label}
            </button>
          ))}
        </nav>

        <div className="live-header-actions">
          <button
            type="button"
            className="live-clock"
            data-tv-control
            data-tv-clock
            data-tv-order="0"
            onClick={() => setClockMode(true)}
            aria-label="Open privacy clock"
          >
            <strong>{now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</strong>
            <span>{now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
          </button>
          <button type="button" data-tv-control data-tv-order="4" className="live-icon-button" onClick={() => loadData()} aria-label="Refresh dashboard" title="Refresh">
            <span className={refreshing ? 'is-spinning' : ''}>↻</span>
          </button>
          <button type="button" data-tv-control data-tv-order="5" className="live-icon-button" onClick={toggleFullscreen} aria-label="Toggle fullscreen" title="Fullscreen">
            {isFullscreen ? '↙' : '↗'}
          </button>
          <Link data-tv-control data-tv-order="6" className="live-close-button" to="/dashboard" aria-label="Return to admin dashboard">×</Link>
        </div>
      </header>

      <section className="live-status-strip">
        <div><span className={`live-signal${error ? ' has-warning' : ''}`} />{error || 'All systems reporting'}</div>
        <span className="live-tv-hint">← → Navigate · OK Select · ↑ ↓ Scroll</span>
        <span>{range.from === range.to ? range.from : `${range.from} — ${range.to}`} · {timezone}</span>
        <span>Updated {updatedAt ? updatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}</span>
      </section>

      <section className={`live-content${loading ? ' is-loading' : ''}`} aria-busy={loading}>
        <div className="live-orders-grid">
          <MetricCard direction="PayIN" summary={data.orders.payin} />
          <MetricCard direction="PayOUT" summary={data.orders.payout} />
        </div>

        <div className="live-liquidity-grid">
          <article className="live-panel live-traders-panel">
            <div className="live-panel-heading">
              <div><span className="live-index">03</span><h2>Trader liquidity</h2></div>
              <span>{data.traderCount} ACTIVE</span>
            </div>
            <div className="live-trader-total"><span>Total balance</span><strong>{formatAmount(data.traders.total)} <small>USDT</small></strong></div>
            <div className="live-split-values">
              <div><span>Current</span><strong>{formatAmount(data.traders.current)}</strong></div>
              <div><span>Pending</span><strong>{formatAmount(data.traders.pending)}</strong></div>
            </div>
          </article>

          <article className="live-panel live-gateways-panel">
            <div className="live-panel-heading">
              <div><span className="live-index">04</span><h2>Gateway balances</h2></div>
              <span>{data.gatewayCount} CONNECTED</span>
            </div>
            <div className={`live-gateway-list ${
              data.gateways.length === 1
                ? 'is-single'
                : data.gateways.length === 2
                  ? 'is-pair'
                  : 'is-multiple'
            }`}>
              {data.gateways.length > 0 ? data.gateways.map((gateway) => (
                <div key={gateway.currency}>
                  <span>{gateway.currency}</span>
                  <strong>{formatAmount(gateway.total)} <small>{gateway.currency}</small></strong>
                </div>
              )) : <p className="live-empty">No gateway balances</p>}
            </div>
          </article>
        </div>

        <article className="live-panel live-wallet-panel">
          <div className="live-panel-heading">
            <div><span className="live-index">05</span><h2>Wallet reserves</h2></div>
            <span>
              TRC20 · {visibleWallets.length} SHOWN ≥10 / {data.wallet?.fundedWalletCount || 0} FUNDED / {data.wallet?.walletCount || 0} CHECKED
              {data.wallet?.updatedAt && (
                <> · {data.wallet.stale ? 'CACHED' : 'UPDATED'} {new Date(data.wallet.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>
              )}
            </span>
          </div>
          <div className="live-wallet-layout">
            <div className="live-wallet-total">
              <span>Total reserve</span>
              <strong>{formatAmount(data.wallet?.total)} <small>USDT</small></strong>
            </div>
            <div className={`live-wallet-list ${
              visibleWallets.length === 1
                ? 'is-single'
                : visibleWallets.length === 2
                  ? 'is-pair'
                  : visibleWallets.length === 3
                    ? 'is-three'
                    : 'is-multiple'
            }`}>
              {visibleWallets.length > 0 ? visibleWallets.map((wallet, index) => (
                <div
                  key={wallet.wallet}
                  style={{ '--wallet-share': `${getPercentageValue(wallet.balance, data.wallet?.total)}%` }}
                >
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <div className="live-wallet-identity">
                    <strong>{wallet.traderName || wallet.traderLogin || wallet.traderId}</strong>
                    <small title={wallet.wallet}>{wallet.wallet}</small>
                  </div>
                  <b>{formatAmount(wallet.balance)} <small>USDT</small></b>
                  <div className="live-wallet-share">
                    <span>{formatPercent(wallet.balance, data.wallet?.total)} of reserve</span>
                    <i aria-hidden="true" />
                  </div>
                </div>
              )) : <p className="live-empty">No wallets with at least 10 USDT</p>}
            </div>
          </div>
        </article>
      </section>
    </main>
  );
};

export default LiveDashboard;
